import React, {useState} from 'react'
import {useSelector} from 'react-redux'
import {Box, Typography, Tabs, Tab, LinearProgress, Chip} from '@mui/material'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import {LoadingButton} from '@mui/lab'
import {subDays} from 'date-fns'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import Loader from '../../common/Loader'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import DateRangePicker from '../DateRangePicker'
import {useGenericPostRequestQuery} from '../../redux/commonRTK'
import {apiConstants} from '../../common/constants'
import useWalletRecharge from '../../hooks/useWalletRecharge'
import {formatNumber, parseDateAndTime} from '../../helpers/UtilityHelper'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, WARNING, ERROR, LIGHT_BG} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'
import {
  pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx,
  helpTextSx, helpTextTypeSx, tabBarSx, dataGridWrapSx, alertStripSx,
} from '../../styles/patterns'
import { secondaryTextSx } from '../orders/list2/cellStyles'

const inr = (v) => `₹${formatNumber(Math.round(Number(v) || 0))}`

const statCardSx = (accent) => ({
  flex: '1 1 200px', minWidth: 180, bgcolor: WHITE, borderRadius: '12px', p: '18px',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  borderTop: `3px solid ${accent}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover},
})

function StatCard({label, value, accent, icon}) {
  return (
    <Box sx={statCardSx(accent)}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: accent, '& svg': {fontSize: 20}}}>
        {icon}
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em'}}>{label}</Typography>
      </Box>
      <Typography sx={{fontFamily: FONT, fontSize: '1.5rem', fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.1}}>{value}</Typography>
    </Box>
  )
}

// ── Transactions ledger ─────────────────────────────────────────
function CreditTransactions() {
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [dateRange, setDateRange] = useState([{endDate: new Date(), startDate: subDays(new Date(), 30), key: 'selection'}])

  const {isLoading, walletRecharge, downloadTransactionFile, isDownloading} =
    useWalletRecharge({dateRange, sizePerPage, page, isPassbook: false})

  const columns = [
    {
      field: 'date', headerName: 'Date & Time', flex: 1.2, headerAlign: 'left', hideSortIcons: true,
      renderCell: ({row}) => <Typography sx={secondaryTextSx}>{parseDateAndTime(row.date)}</Typography>,
    },
    {
      field: 'transactionAmount', headerName: 'Amount', flex: 1, headerAlign: 'left', hideSortIcons: true,
      renderCell: ({row}) => {
        const isCredit = row.credit && row.credit > 0
        const amt = isCredit ? row.credit : row.debit
        return <Typography sx={{...secondaryTextSx, fontWeight: WEIGHT.bold, color: isCredit ? SUCCESS : ERROR}}>{isCredit ? '+' : '-'}{inr(amt)}</Typography>
      },
    },
    {
      field: 'walletBalance', headerName: 'Balance', flex: 1, headerAlign: 'left', hideSortIcons: true,
      renderCell: ({row}) => <Typography sx={secondaryTextSx}>{inr(row.walletBalance)}</Typography>,
    },
    {
      field: 'transactionSource', headerName: 'Source', flex: 1.1, headerAlign: 'left', hideSortIcons: true,
      renderCell: ({row}) => <Typography sx={secondaryTextSx}>{row.transactionSource || 'N/A'}</Typography>,
    },
    {
      field: 'transactionStatus', headerName: 'Status', flex: 0.9, headerAlign: 'left', hideSortIcons: true,
      renderCell: ({row}) => <Typography sx={secondaryTextSx}>{row.transactionStatus || 'N/A'}</Typography>,
    },
    {
      field: 'transactionId', headerName: 'Transaction ID', flex: 1.4, headerAlign: 'left', hideSortIcons: true,
      renderCell: ({row}) => <Typography sx={secondaryTextSx}>{row.transactionId || 'N/A'}</Typography>,
    },
  ]

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2}}>
        <DateRangePicker defaultRange={dateRange} maxDate={new Date()} onChange={(r) => setDateRange(r)} btnColor='primary' />
        <LoadingButton loading={isDownloading} onClick={downloadTransactionFile} variant='outlined' size='small'
          startIcon={<DownloadOutlinedIcon />}
          sx={{fontFamily: FONT, textTransform: 'none', borderRadius: '8px', color: PRIMARY_BLUE, borderColor: PRIMARY_BLUE}}>
          Export CSV
        </LoadingButton>
      </Box>
      <Box sx={dataGridWrapSx}>
        {isLoading ? <Loader /> : (
          <ProzoDataGrid
            columns={columns}
            autoHeight
            rows={(walletRecharge || []).map((row, i) => ({...row, id: i}))}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            setPage={setPage}
            page={page}
            pagination
            hideFooterPagination={false}
            rowCount={Number.MAX_VALUE}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
          />
        )}
      </Box>
    </Box>
  )
}

// ── Main ────────────────────────────────────────────────────────
function Credit() {
  const selectedMerchantId = useSelector((s) => s.user?.selectedUserId)
  const userMerchantId = useSelector((s) => s.user?.userInfo?.merchant?.id)
  const merchantId = selectedMerchantId || userMerchantId
  const [tab, setTab] = useState('overview')

  const {data: walletResp, isFetching} = useGenericPostRequestQuery(
    {type: 'GET', url: apiConstants.GET_ALL_WALLET, data: {merchantId}},
    {skip: !merchantId}
  )

  const wallets = walletResp?.responseObj || []
  const credit = wallets.find((w) => String(w.walletType || '').toLowerCase() === 'postpaid')

  const limit = Number(credit?.creditLimit) || 0
  const due = Number(credit?.dueAmount) || 0
  const available = Math.max(limit - due, 0)
  const balance = Number(credit?.walletBalance) || 0
  const threshold = Number(credit?.alertThreshold) || 0
  const util = limit > 0 ? Math.min((due / limit) * 100, 100) : 0
  const utilColor = util >= 90 ? ERROR : util >= 70 ? WARNING : SUCCESS
  const lowCredit = threshold > 0 && available <= threshold

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Credit</PageTitle>

      <Box sx={pageHeroSx}>
        <Typography sx={heroTitleSx}>Credit</Typography>
        <Typography sx={heroSubSx}>Your postpaid credit line — limit, usage, dues and transaction history.</Typography>
      </Box>

      {isFetching ? (
        <Loader />
      ) : !credit ? (
        <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: 5, textAlign: 'center'}}>
          <Box sx={{width: 64, height: 64, borderRadius: '50%', bgcolor: LIGHT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, color: SUBTLE, '& svg': {fontSize: 30}}}>
            <CreditCardOutlinedIcon />
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No credit line on this account</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, maxWidth: 380, mx: 'auto'}}>
            Your account is on prepaid billing. A credit line appears here once a postpaid wallet is enabled for your merchant.
          </Typography>
        </Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_e, v) => v && setTab(v)} sx={{...tabBarSx, mb: 2.5}}>
            <Tab value='overview' label='Overview' />
            <Tab value='transactions' label='Transactions' />
          </Tabs>

          {tab === 'overview' && (
            <>
              {lowCredit && (
                <Box sx={{...alertStripSx(WARNING), mb: 2}}>
                  <WarningAmberOutlinedIcon sx={{fontSize: 16}} />
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>
                    Available credit ({inr(available)}) has reached your alert threshold ({inr(threshold)}). Clear dues to free up credit.
                  </Typography>
                </Box>
              )}

              <Box sx={{display: 'flex', gap: '14px', flexWrap: 'wrap', mb: 2.5}}>
                <StatCard label='Credit Limit' value={inr(limit)} accent={PRIMARY_BLUE} icon={<CreditCardOutlinedIcon />} />
                <StatCard label='Amount Due' value={inr(due)} accent={due > 0 ? ERROR : SUCCESS} icon={<PaymentsOutlinedIcon />} />
                <StatCard label='Available Credit' value={inr(available)} accent={SUCCESS} icon={<TrendingUpOutlinedIcon />} />
                <StatCard label='Wallet Balance' value={inr(balance)} accent={NAVY} icon={<AccountBalanceWalletOutlinedIcon />} />
              </Box>

              <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: 2.5, mb: 2.5}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>Credit Utilisation</Typography>
                  <Chip label={`${util.toFixed(1)}% used`} size='small'
                    sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: '0.7rem', color: utilColor, bgcolor: `${utilColor}14`}} />
                </Box>
                <LinearProgress variant='determinate' value={util}
                  sx={{height: 10, borderRadius: 5, bgcolor: LIGHT_BG, '& .MuiLinearProgress-bar': {bgcolor: utilColor, borderRadius: 5}}} />
                <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 1}}>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>{inr(due)} used</Typography>
                  <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>{inr(limit)} limit</Typography>
                </Box>
              </Box>

              <Box sx={helpTextSx}>
                <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
                <Typography sx={helpTextTypeSx}>
                  <strong>How it works:</strong> Available credit = Credit Limit − Amount Due. Shipments are billed against your
                  postpaid line; clear dues to restore available credit. See the Transactions tab for the full ledger.
                </Typography>
              </Box>
            </>
          )}

          {tab === 'transactions' && <CreditTransactions />}
        </>
      )}
    </Box>
  )
}

export default withLayout(Credit)
